import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import * as React from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
} from '@/components/shadcn/table';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import SearchIcon from '@/components/ui/icons/SearchIcon.tsx';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export default function RegionsTable<TData, TValue>({
  columns,
  data,
  page,
  setPage,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 100,
      },
    },
  });
  const tableRows = table.getRowModel().rows;
  const rowsNumber: number = table.getCanNextPage() ? tableRows.length : 100;
  const totalRows: number = table.getFilteredRowModel().rows.length;
  const startEntry = page * rowsNumber - rowsNumber + 1;

  const endEntry =
    page * rowsNumber > totalRows
      ? totalRows
      : page * rowsNumber > data.length
        ? data.length
        : page * rowsNumber;

  let resultsMessage;

  if (totalRows === 0) {
    resultsMessage = 'No Results Found';
  } else if (totalRows < rowsNumber) {
    resultsMessage = `${totalRows} Entries Found`;
  } else {
    resultsMessage = `Showing ${startEntry} to ${endEntry} of ${totalRows} Entries`;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPage(1);
    table.getColumn('state')?.setFilterValue(value);
  };

  const nextPage = () => {
    table.nextPage();
    setPage(prevPage => prevPage + 1);
  };

  const previousPage = () => {
    table.previousPage();
    setPage(prevPage => prevPage - 1);
  };

  return (
    <div className="relative h-full overflow-hidden">
      <div className="relative my-3 flex max-w-xs items-center gap-2 rounded border border-neutral-700 bg-neutral-900 px-4 text-neutral-0">
        <SearchIcon className="size-4 fill-current" />
        <Input
          placeholder="Find a country or state/province"
          value={(table.getColumn('state')?.getFilterValue() as string) ?? ''}
          onChange={handleChange}
          className="border-none p-0 leading-none placeholder:text-neutral-700"
        />
      </div>
      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {tableRows?.length ? (
              tableRows.map(row => (
                <TableRow
                  key={row.id}
                  className="leading-none hover:bg-neutral-900"
                  data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex w-full items-center justify-start space-x-4 py-4">
        <div className="flex-1 text-sm text-foreground">{resultsMessage}</div>
        <Button
          variant="dark"
          outline="thin"
          size="sm"
          onClick={previousPage}
          disabled={!table.getCanPreviousPage()}>
          Previous
        </Button>
        <Button
          variant="dark"
          outline="thin"
          size="sm"
          onClick={nextPage}
          disabled={!table.getCanNextPage()}>
          Next
        </Button>
      </div>
    </div>
  );
}
